import React from "react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"

import type {
  BucketClickData,
  DashboardWidget,
  DashboardWidgetConfig,
} from "../../types/core"
import { WidgetFillerContent } from "../WidgetFillerContent"
import { GridWidgetContainer } from "./GridWidgetContainer"

interface GridDashboardWidgetProps {
  widget: DashboardWidget
  dashboardId: string
  onRemove: (id: string) => void
  onConfigure: (id: string) => void
  onClone?: (id: string) => void
  onAlert?: (id: string) => void
  onConfigChange: (id: string, config: DashboardWidgetConfig) => void
  onWidthChange?: (id: string, columns: number) => void
  onPointClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  onChartClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  getSelectedPointForWidget?: (widgetId: string) => BucketClickData | null
}

export const GridDashboardWidget: React.FC<GridDashboardWidgetProps> = ({
  widget,
  dashboardId,
  onRemove,
  onConfigure,
  onClone,
  onAlert,
  onConfigChange,
  onWidthChange,
  onChartClick,
  onPointClick,
  getSelectedPointForWidget,
}) => {
  const { trackEvent } = useAnalytics()

  // Handle point click events for focus mode integration
  const handlePointClick = (
    point: BucketClickData,
    _widgetId: string,
    metricId?: string,
  ) => {
    if (onPointClick) {
      onPointClick(point, widget.id, metricId)

      // Track widget clicked
      trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_CLICKED, {
        widget_id: widget.id,
        widget_type: widget.type,
        metric_id: metricId,
        click_target: "data_point",
      })
    }
  }

  const handleChartClick = (
    point: BucketClickData,
    _widgetId: string,
    metricId?: string,
  ) => {
    if (onChartClick) {
      onChartClick(point, widget.id, metricId)

      trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_CLICKED, {
        widget_id: widget.id,
        widget_type: widget.type,
        metric_id: metricId,
        click_target: "chart",
      })
    }
  }

  // Get selected point for this widget (for focus mode integration)
  const selectedPointInChart = getSelectedPointForWidget?.(widget.id)

  return (
    <GridWidgetContainer
      widget={widget}
      dashboardId={dashboardId}
      onRemove={onRemove}
      onConfigure={onConfigure}
      onClone={onClone}
      onAlert={onAlert}
      onConfigChange={onConfigChange}
      onWidthChange={onWidthChange}
    >
      <WidgetFillerContent
        type={widget.type}
        widget={widget}
        onChartClick={handleChartClick}
        onPointClick={handlePointClick}
        selectedPointInChart={selectedPointInChart}
      />
    </GridWidgetContainer>
  )
}
